import { useState } from "react";
import { Trash2, Upload, AlertTriangle, MapPin, FileText, Loader2 } from "lucide-react";
import PlotMap from "@/components/PlotMap";
import { cn } from "@/lib/utils";
import { ROLES } from "@/lib/i18n";
import { LocationTools, PhotoCapture } from "@/pages/portal/PortalExtras";

export const inputCls = "h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-[15px] outline-none transition-colors focus:border-[#D52B3B] disabled:bg-slate-50";
export const Btn = ({ className, variant = "primary", ...p }) => (
  <button {...p} className={cn("inline-flex h-11 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold transition-colors disabled:opacity-50",
    variant === "primary" ? "bg-[#D52B3B] text-white hover:bg-[#661D1D]" : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50", className)} />
);
export const F = ({ label, children, className }) => (
  <label className={cn("block", className)}>
    <span className="mb-1.5 block text-[13px] font-semibold text-slate-700">{label}</span>
    {children}
  </label>
);

export function CompanyStep({ t, v, set }) {
  const f = (k, type = "text") => <input data-testid={`portal-company-${k}`} type={type} className={inputCls} value={v[k] || ""} onChange={(e) => { const x = e.target.value; set((cur) => ({ ...(cur || {}), [k]: x })); }} />;
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <F label={t.legal_name} className="sm:col-span-2">{f("legal_name")}</F>
      <F label={t.address} className="sm:col-span-2">{f("address")}</F>
      <F label={t.country}>{f("country")}</F>
      <F label={t.operator_status}>
        <select data-testid="portal-company-operator_status" className={inputCls} value={v.operator_status || ""} onChange={(e) => { const x = e.target.value; set((cur) => ({ ...(cur || {}), operator_status: x })); }}>
          <option value="">{t.select}</option>
          {v.operator_status === "eu_trader" && <option value="eu_trader" disabled>{t.op_eu_trader}</option>}
          {ROLES.map((o) => <option key={o} value={o}>{t[`op_${o}`]}</option>)}
        </select>
        {v.operator_status === "eu_trader" && <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-amber-700" data-testid="portal-legacy-trader"><AlertTriangle className="h-3.5 w-3.5 shrink-0" />{t.legacy_trader}</p>}
        <p className="mt-1.5 text-xs text-slate-500" data-testid="portal-role-note">{t.role_note}</p>
      </F>
      <F label={t.registration}>{f("registration")}</F>
      <F label={t.vat}>{f("vat")}</F>
      <F label={t.eori}>{f("eori")}</F>
      <F label={t.contact_name}>{f("contact_name")}</F>
      <F label={t.contact_email}>{f("contact_email", "email")}</F>
      <F label={t.contact_phone}>{f("contact_phone", "tel")}</F>
    </div>
  );
}

export function MaterialsStep({ t, v, set }) {
  if (!v.length) return <p className="text-slate-500">{t.no_materials}</p>;
  const upd = (i, patch) => set((cur) => cur.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">{t.materials_intro}</p>
      {v.map((m, i) => (
        <div key={m.material} className="rounded-xl border border-slate-200 p-4" data-testid={`portal-material-${m.material}`}>
          <p className="font-semibold text-slate-900">{m.description}</p>
          <p className="mt-1 text-xs text-slate-500">{t.ep_material}: <b>{m.material}</b> · {t.vendor_material}: {m.vendor_material || "—"} · {t.hs_code}: <b data-testid={`portal-material-hs-${m.material}`}>{m.hs_code}</b> · {t.commodity}: {m.commodity}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Btn variant={m.confirmed === true ? "primary" : "outline"} data-testid={`portal-material-confirm-${m.material}`} onClick={() => upd(i, { confirmed: true })}>{t.confirm}</Btn>
            <Btn variant={m.confirmed === false ? "primary" : "outline"} data-testid={`portal-material-decline-${m.material}`} onClick={() => upd(i, { confirmed: false })}>{t.decline}</Btn>
          </div>
          {m.confirmed === false && <F label={t.decline_reason} className="mt-3"><input className={inputCls} value={m.decline_reason || ""} onChange={(e) => upd(i, { decline_reason: e.target.value })} /></F>}
          {m.confirmed && (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <F label={t.country_of_production}><input data-testid={`portal-country-${m.material}`} maxLength={2} className={cn(inputCls, "uppercase")} value={m.country_of_production || ""} onChange={(e) => upd(i, { country_of_production: e.target.value.toUpperCase() })} /></F>
              <F label={t.country_of_raw_material}><input data-testid={`portal-origin-${m.material}`} maxLength={2} className={cn(inputCls, "uppercase")} value={m.country_of_raw_material || ""} onChange={(e) => upd(i, { country_of_raw_material: e.target.value.toUpperCase() })} /></F>
              {m.commodity === "Wood" && (
                <>
                  <F label={t.species_scientific}><input data-testid={`portal-species-sci-${m.material}`} className={inputCls} value={m.species_scientific || ""} onChange={(e) => upd(i, { species_scientific: e.target.value })} placeholder="Pinus sylvestris" /></F>
                  <F label={t.species_common}><input data-testid={`portal-species-common-${m.material}`} className={inputCls} value={m.species_common || ""} onChange={(e) => upd(i, { species_common: e.target.value })} /></F>
                </>
              )}
              <F label={t.harvest_from}><input data-testid={`portal-harvest-from-${m.material}`} type="date" className={inputCls} value={m.harvest_from || ""} onChange={(e) => upd(i, { harvest_from: e.target.value })} /></F>
              <F label={t.harvest_to}><input data-testid={`portal-harvest-to-${m.material}`} type="date" className={inputCls} value={m.harvest_to || ""} onChange={(e) => upd(i, { harvest_to: e.target.value })} /></F>
              <F label={t.net_mass_kg}><input data-testid={`portal-net-mass-${m.material}`} type="number" inputMode="decimal" min={0} step="any" className={inputCls} value={m.net_mass_kg ?? ""} onChange={(e) => upd(i, { net_mass_kg: e.target.value === "" ? null : Number(e.target.value) })} /></F>
              {m.commodity === "Wood" && <F label={t.volume_m3}><input data-testid={`portal-volume-${m.material}`} type="number" inputMode="decimal" min={0} step="any" className={inputCls} value={m.volume_m3 ?? ""} onChange={(e) => upd(i, { volume_m3: e.target.value === "" ? null : Number(e.target.value) })} /></F>}
              <F label={t.share_pct_opt}><input data-testid={`portal-share-${m.material}`} type="number" min={0} max={100} className={inputCls} value={m.share_pct ?? ""} onChange={(e) => upd(i, { share_pct: e.target.value === "" ? null : Number(e.target.value) })} /></F>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function PlotForm({ t, geometry, materials, onSave, onCancel, error }) {
  const [name, setName] = useState("");
  const [area, setArea] = useState("");
  const [mats, setMats] = useState(materials.map((m) => m.material));
  return (
    <div className="rounded-xl border-2 border-[#D52B3B]/40 bg-[#D52B3B]/5 p-4" data-testid="portal-plot-form">
      <p className="mb-3 text-sm font-semibold">{geometry.type === "Point" ? t.point : t.polygon}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <F label={t.plot_name}><input data-testid="portal-plot-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} /></F>
        {geometry.type === "Point" && <F label={t.declared_area}><input data-testid="portal-plot-area" type="number" step="0.01" min="0" className={inputCls} value={area} onChange={(e) => setArea(e.target.value)} /></F>}
      </div>
      <p className="mb-1.5 mt-3 text-[13px] font-semibold text-slate-700">{t.linked_materials}</p>
      <div className="flex flex-wrap gap-2">
        {materials.map((m) => (
          <label key={m.material} className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
            <input type="checkbox" checked={mats.includes(m.material)} onChange={() => setMats((x) => (x.includes(m.material) ? x.filter((y) => y !== m.material) : [...x, m.material]))} /> {m.material}
          </label>
        ))}
      </div>
      {error && <p className="mt-3 flex items-center gap-2 text-sm font-medium text-rose-700" data-testid="portal-plot-error"><AlertTriangle className="h-4 w-4" />{error}</p>}
      <div className="mt-4 flex gap-2">
        <Btn data-testid="portal-plot-save" onClick={() => onSave({ name, geometry, declared_area_ha: area === "" ? null : Number(area), materials: mats })}>{t.save_plot}</Btn>
        <Btn variant="outline" onClick={onCancel}>{t.cancel}</Btn>
      </div>
    </div>
  );
}

export function PlotsStep({ t, plots, materials, ro, api, lang }) {
  const [draft, setDraft] = useState(null);
  const [err, setErr] = useState("");
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [poly, setPoly] = useState("");
  const [upl, setUpl] = useState(null);
  const conf = materials.filter((m) => m.confirmed);
  const save = async (body) => {
    setErr("");
    const r = await api.addPlot(body);
    if (r.error) setErr(r.error);
    else setDraft(null);
  };
  const polyFromText = () => {
    const pts = poly.split("\n").map((l) => l.split(/[,;\s]+/).filter(Boolean).map(Number)).filter((p) => p.length >= 2).map(([a, b]) => [b, a]);
    if (pts.length >= 3) setDraft({ type: "Polygon", coordinates: [[...pts, pts[0]]] });
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">{t.plots_intro}</p>
      {!ro && <p className="text-xs text-slate-500">{t.draw_hint}</p>}
      <PlotMap plots={plots} editable={!ro} onDrawn={(g) => { setErr(""); setDraft(g); }} labels={{ street: t.map_street, satellite: t.map_satellite }} height={380} testid="portal-map" lang={lang} />
      {draft && <PlotForm t={t} geometry={draft} materials={conf} onSave={save} onCancel={() => setDraft(null)} error={err} />}
      {!ro && <LocationTools t={t} onPoint={(c) => { setErr(""); setDraft({ type: "Point", coordinates: c }); }} onPolygon={(ring) => { setErr(""); setDraft({ type: "Polygon", coordinates: [ring] }); }} />}
      {!ro && (
        <details className="rounded-xl border border-slate-200 p-4">
          <summary className="cursor-pointer text-sm font-semibold text-slate-800" data-testid="portal-type-coords">{t.type_coords}</summary>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <F label={t.latitude}><input data-testid="portal-lat" className={inputCls} value={lat} onChange={(e) => setLat(e.target.value)} /></F>
            <F label={t.longitude}><input data-testid="portal-lon" className={inputCls} value={lon} onChange={(e) => setLon(e.target.value)} /></F>
            <div className="flex items-end"><Btn variant="outline" className="w-full" data-testid="portal-add-point" onClick={() => lat && lon && setDraft({ type: "Point", coordinates: [Number(lon), Number(lat)] })}>{t.add_point}</Btn></div>
          </div>
          <F label={t.polygon_coords} className="mt-3"><textarea data-testid="portal-poly-text" rows={4} className={cn(inputCls, "h-auto py-2 font-mono text-xs")} value={poly} onChange={(e) => setPoly(e.target.value)} placeholder={"-1.000000, 110.000000\n-1.000000, 110.010000\n-0.990000, 110.010000"} /></F>
          <Btn variant="outline" className="mt-2" onClick={polyFromText} data-testid="portal-add-polygon">{t.add_polygon}</Btn>
        </details>
      )}
      {!ro && (
        <label className="flex cursor-pointer items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-4 text-sm font-medium text-slate-700 hover:bg-slate-50">
          <Upload className="h-4 w-4" /> {t.upload_geo}
          <input type="file" accept=".geojson,.json,.kml,.zip" className="hidden" data-testid="portal-geo-upload" onChange={async (e) => { const f = e.target.files[0]; e.target.value = ""; if (f) setUpl(await api.uploadPlots(f, conf.map((m) => m.material))); }} />
        </label>
      )}
      {upl && (
        <div className="rounded-lg bg-slate-50 p-3 text-sm" data-testid="portal-geo-result">
          {upl.error ? <p className="text-rose-700">{upl.error}</p> : <p>{upl.created.length} {t.uploaded_n}</p>}
          {upl.rejected?.map((r, i) => <p key={i} className="text-rose-700">{t.rejected}: {r.name} – {r.reason}</p>)}
        </div>
      )}
      <div className="space-y-2" data-testid="portal-plot-list">
        {!plots.length && <p className="text-sm text-slate-500">{t.no_plots}</p>}
        {plots.map((p) => (
          <div key={p.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5 text-sm">
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#D52B3B]" /><b>{p.name}</b> · {p.kind === "point" ? t.point : t.polygon} · {t.area} <span data-testid={`portal-plot-area-${p.id}`}>{p.area_ha}</span> {t.hectares} · {p.materials.join(", ")}</span>
            {!ro && <button onClick={() => api.deletePlot(p.id)} className="rounded p-1.5 text-slate-400 hover:text-rose-600" aria-label={t.delete}><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function DocsStep({ t, docs, types, ro, api }) {
  const [type, setType] = useState("fsc_pefc");
  const [expiry, setExpiry] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const up = async (f) => {
    setErr("");
    if (f.size > 20 * 1024 * 1024) return setErr(t.file_too_large);
    setBusy(true);
    const r = await api.uploadDoc(f, type, expiry);
    setBusy(false);
    if (r.error) setErr(r.error);
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">{t.docs_intro}</p>
      {!ro && (
        <div className="grid gap-3 rounded-xl border border-slate-200 p-4 sm:grid-cols-3">
          <F label={t.doc_type}><select data-testid="portal-doc-type" className={inputCls} value={type} onChange={(e) => setType(e.target.value)}>{types.map((d) => <option key={d} value={d}>{t[`dt_${d}`]}</option>)}</select></F>
          <F label={t.expiry}><input data-testid="portal-doc-expiry" type="date" className={inputCls} value={expiry} onChange={(e) => setExpiry(e.target.value)} /></F>
          <div className="flex items-end">
            <label className={cn("inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-[#D52B3B] px-4 text-sm font-semibold text-white hover:bg-[#661D1D]", busy && "opacity-60")}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {busy ? t.uploading : t.choose_file}
              <input type="file" className="hidden" data-testid="portal-doc-file" disabled={busy} onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) up(f); }} />
            </label>
          </div>
          {err && <p className="flex items-center gap-2 text-sm font-medium text-rose-700 sm:col-span-3" data-testid="portal-doc-error"><AlertTriangle className="h-4 w-4" />{err}</p>}
          <div className="sm:col-span-3"><PhotoCapture t={t} busy={busy} onPdf={up} /></div>
        </div>
      )}
      <div className="space-y-2" data-testid="portal-doc-list">
        {!docs.length && <p className="text-sm text-slate-500">{t.no_docs}</p>}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5 text-sm">
            <span className="flex min-w-0 items-center gap-2"><FileText className="h-4 w-4 shrink-0 text-slate-400" /><span className="truncate"><b>{t[`dt_${d.doc_type}`]}</b> · {d.filename}{d.expiry ? ` · ${t.expiry.split(" (")[0]} ${d.expiry}` : ""}</span></span>
            {!ro && <button onClick={() => api.deleteDoc(d.id)} className="rounded p-1.5 text-slate-400 hover:text-rose-600" aria-label={t.delete}><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}

const DDS_RE = /^\d{2}[A-Z]{2}[A-Z0-9]{8,}$/;

export function DdsStep({ t, v, set }) {
  const conf = v.filter((m) => m.confirmed);
  if (!conf.length) return <p className="text-slate-500">{t.no_confirmed}</p>;
  const upd = (mat, patch) => set((cur) => cur.map((m) => (m.material === mat ? { ...m, ...patch } : m)));
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">{t.dds_intro}</p>
      {conf.map((m) => (
        <div key={m.material} className="rounded-xl border border-slate-200 p-4">
          <p className="font-semibold text-slate-900">{m.material} · {m.description}</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <F label={t.dds_reference}><input data-testid={`portal-dds-ref-${m.material}`} className={cn(inputCls, "font-mono uppercase")} value={m.dds_reference || ""} onChange={(e) => upd(m.material, { dds_reference: e.target.value.toUpperCase() })} /></F>
            <F label={t.dds_verification}><input data-testid={`portal-dds-ver-${m.material}`} className={cn(inputCls, "font-mono")} value={m.dds_verification || ""} onChange={(e) => upd(m.material, { dds_verification: e.target.value })} /></F>
          </div>
          {m.dds_reference && !DDS_RE.test(m.dds_reference) && <p className="mt-2 flex items-center gap-2 text-sm text-amber-700" data-testid={`portal-dds-warn-${m.material}`}><AlertTriangle className="h-4 w-4" />{t.dds_format_warn}</p>}
        </div>
      ))}
    </div>
  );
}
